import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";

export const replayTour = () => window.dispatchEvent(new Event("epil-tour"));

const visible = (sel) => [...document.querySelectorAll(sel)].find((el) => el.offsetParent !== null);

export function QuickTour() {
  const [steps, setSteps] = useState(null);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const start = useCallback(async (force) => {
    const { data } = await api.get("/help");
    if (!force && data.tour_seen) return;
    const avail = data.tour.filter((s) => visible(`[data-testid="${s.target}"], [data-testid="mobile-${s.target}"]`));
    if (avail.length) { setI(0); setSteps(avail); }
  }, []);
  useEffect(() => {
    const t = setTimeout(() => start(false).catch(() => {}), 900);
    const h = () => start(true).catch(() => {});
    window.addEventListener("epil-tour", h);
    return () => { clearTimeout(t); window.removeEventListener("epil-tour", h); };
  }, [start]);
  useLayoutEffect(() => {
    if (!steps) return;
    const el = visible(`[data-testid="${steps[i].target}"], [data-testid="mobile-${steps[i].target}"]`);
    el?.scrollIntoView({ block: "nearest", inline: "center" });
    const upd = () => el && setRect(el.getBoundingClientRect());
    upd(); window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, [steps, i]);
  if (!steps || !rect) return null;
  const close = () => { setSteps(null); api.post("/help/tour", { seen: true }).catch(() => {}); };
  const s = steps[i], last = i === steps.length - 1;
  const left = Math.max(12, Math.min(rect.left, window.innerWidth - 312));
  return (
    <div className="fixed inset-0 z-[60]" data-testid="quick-tour">
      <div className="absolute rounded-lg ring-4 ring-primary transition-all duration-300" style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8, boxShadow: "0 0 0 9999px rgba(29,29,27,0.55)" }} />
      <div className="absolute w-[300px] rounded-xl border bg-card p-4 shadow-2xl animate-in fade-in slide-in-from-top-2" style={{ top: rect.bottom + 14, left }} data-testid="quick-tour-card">
        <button className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:bg-muted" onClick={close} aria-label="Close tour" data-testid="quick-tour-close"><X className="h-4 w-4" /></button>
        <div className="eyebrow">Quick tour · {i + 1} of {steps.length}</div>
        <div className="mt-1 font-heading text-base font-bold" data-testid="quick-tour-title">{s.title}</div>
        <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
        <div className="mt-3 flex justify-between gap-2">
          <Button size="sm" variant="ghost" onClick={close} data-testid="quick-tour-skip">Skip tour</Button>
          <div className="flex gap-2">{i > 0 && <Button size="sm" variant="outline" onClick={() => setI(i - 1)} data-testid="quick-tour-back">Back</Button>}
            <Button size="sm" onClick={() => (last ? close() : setI(i + 1))} data-testid="quick-tour-next">{last ? "Done" : "Next"}</Button></div>
        </div>
      </div>
    </div>
  );
}
